import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Global Footer (used on Landing, Donate, CMS pages).
 * Loads CMS pages list to build the menu sections.
 */
export default function Footer() {
    const [pages, setPages] = useState([]);

    useEffect(() => {
        let active = true;
        axios.get(`${API}/pages`)
            .then(r => { if (active) setPages((r.data.items || []).filter(p => !p.slug.endsWith('-en'))); })
            .catch(() => { /* silent — footer must never break the page */ });
        return () => { active = false; };
    }, []);

    const about360 = pages.filter(p => p.group_key === 'about360')
        .sort((a, b) => (a.position || 0) - (b.position || 0));
    const legal = pages.filter(p => p.group_key === 'legal')
        .sort((a, b) => (a.position || 0) - (b.position || 0));

    return (
        <footer className="bg-foreground text-background mt-16" data-testid="site-footer">
            {/* Identity & contact strip */}
            <div className="border-b border-background/10">
                <div className="max-w-6xl mx-auto px-6 py-10 grid md:grid-cols-12 gap-8">
                    <div className="md:col-span-5">
                        <div className="flex items-center gap-3 mb-3">
                            <img src="/brand/african-valley-logo-256.png" alt="African Valley · Projet 360°"
                                 className="h-12 w-12 object-contain rounded shrink-0" draggable={false} />
                            <div>
                                <div className="flex items-baseline gap-2">
                                    <span className="font-heading text-2xl font-semibold tracking-tight">African</span>
                                    <span className="font-heading text-2xl text-primary font-medium">Valley</span>
                                </div>
                                <div className="text-[10px] uppercase tracking-[0.22em] text-background/55 mt-0.5">Projet 360°</div>
                            </div>
                        </div>
                        <p className="text-sm text-background/70 italic leading-relaxed mb-4" data-testid="footer-brand-tagline">
                            Marque exploitée par DMF.
                        </p>
                        <div className="space-y-1.5 text-xs text-background/60">
                            <div className="text-background/50 pt-1.5">SIRET 99174107500014 · Code APE 9003B</div>
                        </div>
                    </div>

                    {/* About 360 */}
                    <div className="md:col-span-3">
                        <div className="text-xs uppercase tracking-[0.18em] text-background/40 font-semibold mb-4">
                            African Valley Projet 360
                        </div>
                        <ul className="space-y-2.5 text-sm">
                            {about360.length === 0 && (
                                <li className="text-background/40 text-xs">—</li>
                            )}
                            {about360.map(p => (
                                <li key={p.slug}>
                                    <Link to={`/p/${p.slug}`} className="text-background/75 hover:text-primary transition-colors" data-testid={`footer-link-${p.slug}`}>
                                        {p.title_fr}
                                    </Link>
                                </li>
                            ))}
                        </ul>
                    </div>

                    {/* Légal */}
                    <div className="md:col-span-2">
                        <div className="text-xs uppercase tracking-[0.18em] text-background/40 font-semibold mb-4">Légal</div>
                        <ul className="space-y-2.5 text-sm">
                            {legal.length === 0 && (
                                <li className="text-background/40 text-xs">—</li>
                            )}
                            {legal.map(p => (
                                <li key={p.slug}>
                                    <Link to={`/p/${p.slug}`} className="text-background/75 hover:text-primary transition-colors" data-testid={`footer-link-${p.slug}`}>
                                        {p.title_fr}
                                    </Link>
                                </li>
                            ))}
                        </ul>
                    </div>

                    {/* CTAs */}
                    <div className="md:col-span-2 flex flex-col gap-3">
                        <div className="text-xs uppercase tracking-[0.18em] text-background/40 font-semibold mb-2">Plateforme</div>
                        <Link to="/apply?source=footer" className="px-4 py-2.5 rounded-full bg-primary text-primary-foreground text-sm text-center hover:bg-primary-hover transition-colors" data-testid="footer-apply">
                            Demander un accès
                        </Link>
                        <Link to="/p/diagnostic" className="px-4 py-2.5 rounded-full border border-background/20 text-sm text-center hover:bg-background/5 transition-colors" data-testid="footer-diagnostic">
                            Diagnostic gratuit
                        </Link>
                        <Link to="/p/campus" className="px-4 py-2.5 rounded-full border border-background/20 text-sm text-center hover:bg-background/5 transition-colors" data-testid="footer-campus">
                            Campus 360° 🎓
                        </Link>
                        <Link to="/p/partenaires" className="px-4 py-2.5 rounded-full border border-background/20 text-sm text-center hover:bg-background/5 transition-colors" data-testid="footer-partenaires">
                            Partenaires & financeurs
                        </Link>
                        <Link to="/donate" className="text-xs text-center text-background/60 hover:text-background transition-colors pt-1" data-testid="footer-donate">
                            ❤ Soutenir
                        </Link>
                        <Link to="/auth/signin" className="text-xs text-center text-background/60 hover:text-background transition-colors">
                            Espace client →
                        </Link>
                    </div>
                </div>
            </div>

            {/* Copyright strip */}
            <div className="max-w-6xl mx-auto px-6 py-5 flex flex-col sm:flex-row items-center justify-between gap-2 text-[11px] text-background/40">
                <div>© {new Date().getFullYear()} African Valley — Tous droits réservés. Marque déposée.</div>
                <div className="uppercase tracking-[0.22em]">Plateforme africaine · DeepSec</div>
            </div>
        </footer>
    );
}
